import {
  Film,
  Image,
  Instagram,
  Link2,
  ListMusic,
  Megaphone,
  Music4,
  Smartphone,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import IconChip from './IconChip'

/* One release, and everything generated from it.
 *
 * The argument of the creative page is that these tools are worth having
 * BECAUSE they live inside the release — the platform already knows the title,
 * the artist, the date, the artwork and the store links, so the assets come out
 * of it rather than being typed in again somewhere else.
 *
 * A list of nine tools makes the opposite point: it looks like nine products
 * you would have to buy and wire together. Drawing them as nine things that
 * fall out of ONE record is the claim, and the shape carries it.
 *
 * The stagger is deliberate and cheap: each spoke fades in on its own delay so
 * the eye reads it as generation rather than as a grid that was always there.
 * app/assets/tailwind/application.css stops it under prefers-reduced-motion.
 */

interface Asset {
  key: string
  label: string
  note: string
  icon: LucideIcon
}

const ASSETS: Asset[] = [
  { key: 'cover', label: 'Cover artwork', note: 'Generated or uploaded', icon: Image },
  { key: 'motion', label: 'Motion artwork', note: 'The cover, animated', icon: Sparkles },
  { key: 'canvas', label: 'Vertical loop', note: 'For the players that take one', icon: Smartphone },
  { key: 'lyric', label: 'Lyric video', note: 'From the track’s own lyrics', icon: Music4 },
  { key: 'promo', label: 'Promo video', note: 'Cut for social', icon: Film },
  { key: 'post', label: 'Social posts', note: 'Square, story and reel cover', icon: Instagram },
  { key: 'link', label: 'Smart link', note: 'Routes to the right store', icon: Link2 },
  { key: 'presave', label: 'Pre-save page', note: 'Converts on release day', icon: ListMusic },
  { key: 'pitch', label: 'Editorial pitch', note: 'Drafted for submission', icon: Megaphone },
]

export default function ReleaseAssetFan() {
  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 sm:p-9">
      <div className="text-center">
        <p className="font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-[#ff8c4d]">
          One record in
        </p>
        <h3
          className="mx-auto mt-2 max-w-xl text-lg font-semibold leading-snug text-white sm:text-xl"
          style={{ fontFamily: 'var(--wavbee-font-display)' }}
        >
          Nine things out, and nobody retyped the title.
        </h3>
      </div>

      {/* The source record. */}
      <div className="mx-auto mt-8 flex max-w-sm items-center gap-4 rounded-2xl border border-[#ff6a1a]/25 bg-[#ff6a1a]/[0.07] p-4">
        <div
          className="h-14 w-14 shrink-0 rounded-lg bg-gradient-to-br from-[#ff6a1a]/60 to-[#ff6a1a]/15"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="truncate text-[0.9375rem] font-semibold text-white">Slow Monsoon</p>
          <p className="truncate text-[0.75rem] text-white/45">Ana Reyes · 14 March</p>
          <p className="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-[#ff8c4d]/70">
            One release record
          </p>
        </div>
      </div>

      {/* Fan-out. A single spine on mobile, three columns above it. */}
      <div className="relative mt-6">
        <div
          className="mx-auto mb-6 h-8 w-px bg-gradient-to-b from-[#ff6a1a]/50 to-[#ff6a1a]/10"
          aria-hidden="true"
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ASSETS.map((asset, index) => {
            const Icon = asset.icon
            return (
              <div
                key={asset.key}
                className="tech-asset-card flex items-start gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4"
                style={{ animationDelay: `${index * 0.07}s` }}
              >
                <IconChip icon={Icon} />
                <div className="min-w-0">
                  <p className="text-[0.8125rem] font-semibold text-white/85">{asset.label}</p>
                  <p className="mt-0.5 text-[0.75rem] leading-relaxed text-white/40">
                    {asset.note}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <p className="mt-6 text-center text-[0.75rem] leading-relaxed text-white/45">
        Every one of these needs the same five facts, and the release already holds all five. That is
        the whole reason they belong in here rather than in nine browser tabs.
      </p>
    </div>
  )
}
